import React, { ReactNode } from 'react'
import {
    DataTable,
    DataTableOpenPanelButton,
    DataTableScope,
    DataTableToolbar,
    getNoMatchesText,
    useDataTableFilters,
    useDataTableFiltersState,
    useNestedDataTableState,
} from '../../../../shared/components/DataTable'
import { EmptyPlaceholder } from '../../../../shared/components/EmptyPlaceholder'
import { useLinkedRecordsExpansion } from '../../../../shared/components/LinkedRecordsPanel'
import useActivityStatuses from '../../../../shared/hooks/useActivityStatuses'
import useActivitiesDisplayName from '../../../../shared/hooks/useDisplayNames/variants/useActivitiesDisplayName'
import { useIsRoadmapReadonly } from '../../../../shared/hooks/useIsRoadmapReadonly'
import { useIsSnapshotMode } from '../../../../shared/hooks/useIsSnapshotMode'
import LinkedStrategicImperativesPanel from '../../../StrategicImperatives/StrategicImperativeTable/components/LinkedStrategicImperativesPanel'
import { StrategicImperativeColumnId } from '../../../StrategicImperatives/StrategicImperativeTable/StrategicImperativeTable.types'
import LinkedValueMessagesPanel from '../../../ValueMessages/ValueMessageTable/components/LinkedValueMessagesPanel'
import { ValueMessageColumnId } from '../../../ValueMessages/ValueMessageTable/ValueMessageTable.types'
import {
    ACTIVITY_STRATEGIC_IMPERATIVES_TABLE_SCOPE,
    ACTIVITY_VALUE_MESSAGES_TABLE_SCOPE,
} from '../ActivityTable.constants'
import { getActivityColumns } from '../ActivityTable.columns'
import { getActivityFilterDefinitions, getActivitySearchValues } from '../ActivityTable.filters'
import { ActivityColumnId, ActivityExpandedPanel, ActivityRow } from '../ActivityTable.types'
import { useActivityDisplayNames } from '../useActivityDisplayNames'
import { renderActivityCell } from './renderActivityCell'

export type ActivityTabTableProps = {
    rows: ActivityRow[]
    scope: DataTableScope
    defaultColumnIds: ReadonlySet<ActivityColumnId>
    searchPlaceholder: string
    emptyTitle: string
    emptySubtitle: ReactNode
    noMatchesLabel: string
    onOpenPanel: (activityId: string) => void
    onReorder?: (rows: ActivityRow[]) => void
}

export const ActivityTabTable = ({
    rows,
    scope,
    defaultColumnIds,
    searchPlaceholder,
    emptyTitle,
    emptySubtitle,
    noMatchesLabel,
    onOpenPanel,
    onReorder,
}: ActivityTabTableProps) => {
    const { getActivityStatusDetails } = useActivityStatuses()
    const displayNames = useActivityDisplayNames()
    const activityLabel = useActivitiesDisplayName().singular
    const isSnapshotMode = useIsSnapshotMode()
    const isRoadmapReadonly = useIsRoadmapReadonly()

    const filtersState = useDataTableFiltersState(scope)
    const linkedValueMessagesFiltersState = useDataTableFiltersState(ACTIVITY_VALUE_MESSAGES_TABLE_SCOPE)
    const linkedValueMessagesTableState = useNestedDataTableState<ValueMessageColumnId>()
    const linkedStrategicImperativesFiltersState = useDataTableFiltersState(ACTIVITY_STRATEGIC_IMPERATIVES_TABLE_SCOPE)
    const linkedStrategicImperativesTableState = useNestedDataTableState<StrategicImperativeColumnId>()

    const linkedRecordsExpansion = useLinkedRecordsExpansion<ActivityExpandedPanel>(displayNames)

    const { toolbarProps, hiddenColumnIds, hasActiveFilters, visibleRows } = useDataTableFilters({
        rows,
        filterDefinitions: getActivityFilterDefinitions(getActivityStatusDetails, displayNames, defaultColumnIds),
        getSearchValues: getActivitySearchValues,
        state: filtersState,
    })

    return (
        <DataTable
            showIndexColumn
            hasStickyColumns
            columns={getActivityColumns(displayNames)}
            rows={visibleRows}
            getRowId={(activity) => activity._id}
            renderCell={renderActivityCell(getActivityStatusDetails, linkedRecordsExpansion)}
            hiddenColumnIds={hiddenColumnIds}
            renderRowActions={(activity) => (
                <DataTableOpenPanelButton
                    accessibleLabel={`Open ${activity.title}`}
                    onClick={() => onOpenPanel(activity._id)}
                />
            )}
            expandedRowId={linkedRecordsExpansion.expandedRow?.id ?? null}
            renderExpandedContent={(activity) =>
                linkedRecordsExpansion.expandedRow?.panel === 'strategicImperatives' ? (
                    <LinkedStrategicImperativesPanel
                        ownerName={activity.title}
                        ownerLabel={activityLabel}
                        linkedIds={activity.linkedStrategicImperatives}
                        filtersState={linkedStrategicImperativesFiltersState}
                        state={linkedStrategicImperativesTableState}
                    />
                ) : (
                    <LinkedValueMessagesPanel
                        ownerName={activity.title}
                        ownerLabel={activityLabel}
                        linkedIds={activity.linkedValueMessages}
                        filtersState={linkedValueMessagesFiltersState}
                        state={linkedValueMessagesTableState}
                    />
                )
            }
            reorder={
                onReorder && {
                    isDisabled: isSnapshotMode || isRoadmapReadonly || hasActiveFilters,
                    onReorder,
                }
            }
            toolbar={<DataTableToolbar {...toolbarProps} searchPlaceholder={searchPlaceholder} filterMenuWidth={260} />}
            emptyState={
                <EmptyPlaceholder
                    title={hasActiveFilters ? getNoMatchesText(noMatchesLabel) : emptyTitle}
                    subtitle={hasActiveFilters ? 'Clear the search or filters to see every row again.' : emptySubtitle}
                    sx={{ minHeight: 280 }}
                />
            }
        />
    )
}

export default ActivityTabTable
